/**
 * Shared constants + copy for the Praetorian-only permanent ("hard") delete.
 *
 * @module components/actions/constants/hardDelete
 */

/**
 * Maximum number of explicitly-selected keys a single bulk hard-delete may
 * target. Mirrors the backend `bulktypes.MaxHardDeleteKeys` cap. Bulk hard
 * delete is explicit-selection-only (never "select all matching") and is
 * disabled above this count to bound the irreversible blast radius.
 */
export const MAX_HARD_DELETE_KEYS = 1000;

/**
 * Single source of truth for the reason a hard-delete control is gated.
 *
 * Every hard-delete surface (asset toolbar, seeds toolbar, and the
 * vulnerability manage dropdown) derives its disabled/tooltip copy from here
 * so the wording can never drift across entities again.
 *
 * @returns the human-readable reason the control is disabled, or `null` when
 *   the current selection is eligible for permanent deletion.
 */
export function getHardDeleteDisabledReason({
  isSelectAll,
  selectedCount,
}: {
  /** True when the selection is "select all matching" (query mode). */
  isSelectAll: boolean;
  /** Number of explicitly-selected rows. */
  selectedCount: number;
}): string | null {
  if (isSelectAll) {
    return 'Permanent delete is not available for "Select all matching" — select specific rows instead.';
  }
  if (selectedCount > MAX_HARD_DELETE_KEYS) {
    return `Permanent delete is limited to ${MAX_HARD_DELETE_KEYS} items at a time.`;
  }
  return null;
}
